import React, { useState } from 'react';
import { Search, CheckCircle, X, Plug, ExternalLink } from 'lucide-react';

// Integration options with timing info
const integrationOptionsWithTiming = [
  // Calendar - During Call
  { id: 'cal-com', name: 'Cal.com', category: 'Calendar', timing: 'during', icon: '📅', description: 'Sync your Cal.com calendar to allow voice assistants to schedule meetings on your behalf.' },
  { id: 'calendly', name: 'Calendly', category: 'Calendar', timing: 'during', icon: '📆', description: 'Connect your Calendly account to check availability and schedule appointments through your voice assistants.' },
  { id: 'google-calendar', name: 'Google Calendar', category: 'Calendar', timing: 'during', icon: '📅', description: 'Connect your Google Calendar to check availability and schedule appointments through your voice assistants.' },
  
  // Custom - During Call
  { id: 'custom-api', name: 'Custom API', category: 'Custom', timing: 'during', icon: '⚙️', description: 'Connect to any custom API endpoint to extend your assistant\'s capabilities with external data and services.' },
  
  // Database - During Call & Post Call
  { id: 'google-sheets-during', name: 'Google Sheets', category: 'Database', timing: 'during', icon: '📊', description: 'Connect your Google Sheets to read, write, and manage spreadsheet data during calls.' },
  { id: 'google-sheets-post', name: 'Google Sheets', category: 'Database', timing: 'post', icon: '📊', description: 'Connect your Google Sheets to read, write, and manage spreadsheet data through your voice assistants.' },
  
  // CRM - Post Call
  { id: 'salesforce', name: 'Salesforce', category: 'CRM', timing: 'post', icon: '☁️', description: 'Connect your Salesforce CRM to access customer data, manage leads, and update records through your voice assistants.' },
  { id: 'hubspot', name: 'HubSpot', category: 'CRM', timing: 'post', icon: '🔶', description: 'Connect your HubSpot platform to enable voice assistants to manage contacts, automate marketing campaigns, and handle customer service tasks.' },
  
  // Communication - Post Call & During Call
  { id: 'slack', name: 'Slack', category: 'Communication', timing: 'post', icon: '💬', description: 'Connect your Slack workspace to receive notifications and updates about your voice assistants.' },
  { id: 'whatsapp', name: 'WhatsApp Cloud', category: 'Communication', timing: 'during', icon: '📱', description: 'Send WhatsApp messages during calls using Meta Cloud API templates via your connected Cloud WhatsApp number.' },
  
  // Contact Center - Post Call
  { id: 'genesys', name: 'Genesys', category: 'Contact Center', timing: 'post', icon: '📞', description: 'Connect your Genesys Cloud contact center to enhance customer experience with AI-powered routing, real-time analytics, and seamless voice AI assistant integration.' },
];

const IntegrationsTab = ({ agent, setAgent, hasFeature }) => {
  const [integrationSearch, setIntegrationSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = ['all', ...new Set(integrationOptionsWithTiming.map(i => i.category))];

  const filteredIntegrations = integrationOptionsWithTiming.filter(integration => {
    const matchesSearch = integration.name.toLowerCase().includes(integrationSearch.toLowerCase()) ||
                          integration.description.toLowerCase().includes(integrationSearch.toLowerCase());
    const matchesCategory = activeCategory === 'all' || 
                           integration.category.toLowerCase() === activeCategory.toLowerCase();
    return matchesSearch && matchesCategory;
  });

  const toggleIntegration = (integrationId) => {
    setAgent(prev => ({
      ...prev,
      integrations: prev.integrations.includes(integrationId)
        ? prev.integrations.filter(id => id !== integrationId)
        : [...prev.integrations, integrationId]
    }));
  };

  if (!hasFeature('integrations_marketplace')) {
    return (
      <div className="space-y-6">
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-8 text-center">
          <Plug className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Integrations Disabled</h3>
          <p className="text-gray-400">This feature has been disabled by your administrator.</p>
          <p className="text-gray-500 text-sm mt-2">Contact your admin to enable Integrations access.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with info icon */}
      <div className="flex items-center gap-2 mb-4">
        <h2 className="text-xl font-semibold text-white">Connect New Integrations</h2>
        <span className="w-5 h-5 rounded-full border border-gray-500 inline-flex items-center justify-center text-xs text-gray-500">i</span>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            value={integrationSearch}
            onChange={(e) => setIntegrationSearch(e.target.value)}
            placeholder="Search integrations..."
            className="w-full bg-[#0d1117] border border-gray-800 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-gray-500 outline-none focus:border-teal-400"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${
                activeCategory === cat
                  ? 'bg-teal-400 text-black'
                  : 'bg-[#1a1a1f] text-gray-300 hover:bg-[#252530]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Active Integrations */}
      {agent.integrations.length > 0 && (
        <section className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            Active Integrations ({agent.integrations.length})
          </h3>
          <div className="flex flex-wrap gap-2">
            {agent.integrations.map((intId) => {
              const integration = integrationOptionsWithTiming.find(i => i.id === intId);
              return integration ? (
                <div 
                  key={intId}
                  className="flex items-center gap-2 bg-teal-400/10 border border-teal-400/30 rounded-full px-3 py-1.5"
                >
                  <span className="text-lg">{integration.icon}</span>
                  <span className="text-teal-400 text-sm font-medium">{integration.name}</span>
                  <button
                    onClick={() => toggleIntegration(intId)}
                    className="text-teal-400 hover:text-red-400 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : null;
            })}
          </div>
        </section>
      )}

      {/* All Integrations Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredIntegrations.map((integration) => {
          const isActive = agent.integrations.includes(integration.id);
          return (
            <div 
              key={integration.id}
              className="bg-[#0d1117] border border-gray-800 rounded-xl p-6 hover:border-gray-700 transition-colors"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-xl">
                    {integration.icon}
                  </div>
                  <span className="text-white font-semibold">{integration.name}</span>
                </div>
                {/* Timing Badge */}
                <span className={`px-2 py-1 text-xs font-medium rounded border ${
                  integration.timing === 'during' 
                    ? 'text-green-400 border-green-500/50 bg-green-500/10' 
                    : 'text-blue-400 border-blue-500/50 bg-blue-500/10'
                }`}>
                  {integration.timing === 'during' ? 'During Call' : 'Post Call'} ⓘ
                </span>
              </div>
              
              <p className="text-gray-400 text-sm mb-6 min-h-[60px]">
                {integration.description}
              </p>
              
              <button
                onClick={() => toggleIntegration(integration.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive 
                    ? 'bg-teal-400/10 text-teal-400 border border-teal-400/30' 
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {isActive ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Connected
                  </>
                ) : (
                  <>
                    Connect
                    <ExternalLink className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default IntegrationsTab;
